/* eslint-disable complexity */
/* eslint-disable extra-rules/no-commented-out-code,max-lines */
// 9fbef606107a605d69c0edbcd8029e5d
import React from 'react';
import dynamic from 'next/dynamic';
import PropTypes from 'prop-types';
import throttle from 'lodash/throttle';
import debounce from 'lodash/debounce';
import isEqual from 'lodash/isEqual';
import withStyles from '@tcp/core/src/components/common/hoc/withStyles';
import LabelsContext from '@tcp/core/src/components/common/context/LabelsContext';
import errorBoundary from '@tcp/core/src/components/common/hoc/withErrorBoundary/errorBoundary';
import { getViewportInfo, getAPIConfig } from '@tcp/core/src/utils';
import { refreshPage } from '@tcp/core/src/utils/utils.web';
import { NAVIGATION_VISIBLE } from '@tcp/core/src/constants/rum.constants';
import RenderPerf from '@tcp/web/src/components/common/molecules/RenderPerf';
import DynamicModule from '@tcp/core/src/components/common/atoms/DynamicModule';
import { getLocalStorage } from '@tcp/core/src/utils/localStorageManagement';
import { isShopThisStoreModeOn } from '@tcp/core/src/reduxStore/selectors/session.selectors';
import { SHOP_THIS_STORE_ACCESSIBLE_COMPONENTS } from '@tcp/core/src/constants/shopThisStore.constant';
import Notification from '@tcp/core/src/components/common/molecules/Notification';
import { flushSync } from 'react-dom';
import {
  getLabelFromCopyOrFromTranslation,
  renderLabel,
  getDynamicLabelValues,
} from '@tcp/core/src/utils/labels/labelUtilty';
import HeaderShopThisStoreSkeleton from '../molecules/HeaderShopThisStoreArea/skeleton/HeaderShopThisStoreSkeleton';
import HeaderLabels, { GLOBAL_LABELS } from '../labels/HeaderNew.labels';
import HeaderMiddleNav from '../molecules/HeaderMiddleNav';
import style, { HeaderTag, SkipMain } from '../Header.style';

const COUNTDOWN_POSITION_TOP_NAV = 'topnavwrapper';
const COUNTDOWN_POSITION_SUB_NAV = 'subnav';

const SLIDER_POSITION_TOP_NAV = 'topnavwrapper';
const SLIDER_POSITION_SUB_NAV = 'subnav';

const CondensedHeader = dynamic(() =>
  import(/* webpackChunkName: "condensed-header" */ '../molecules/CondensedHeader')
);

const HeaderShopThisStoreAreaCondensed = dynamic(() =>
  import(
    /* webpackChunkName: "header-shop-this-store" */ '../molecules/HeaderShopThisStoreAreaCondensed'
  )
);

const HeaderPromo = dynamic(() =>
  import(/* webpackChunkName: "header-promo" */ '../molecules/HeaderPromo')
);

const ModuleCountDown = dynamic(() =>
  import(
    /* webpackChunkName: "module-count-down" */ '@tcp/core/src/components/common/molecules/ModuleCountDown'
  )
);

const ModuleSlider = dynamic(() =>
  import(
    /* webpackChunkName: "module-slider" */ '@tcp/core/src/components/common/molecules/ModuleSlider'
  )
);

const HeaderShopThisStoreArea = dynamic(
  () =>
    import(/* webpackChunkName: "header-shop-this-store" */ '../molecules/HeaderShopThisStoreArea'),
  {
    loading: () => <HeaderShopThisStoreSkeleton />,
    ssr: false,
  }
);

const LoyaltyPromoBanner = dynamic(() =>
  import(
    /* webpackChunkName: "loyalty-promo-banner" */ '@tcp/core/src/components/common/molecules/LoyaltyPromoBanner'
  )
);

const doesCondensedClassExists = (condensedHeader) =>
  condensedHeader &&
  condensedHeader.classList &&
  condensedHeader.classList.contains('show-condensed-header');

class Header extends React.Component {
  globalLabels = getLabelFromCopyOrFromTranslation(GLOBAL_LABELS, HeaderLabels);

  placeCashDesktopUrl = getDynamicLabelValues('dynamic.global_d.lbl_header_bgImage_desktop_url');

  placeCashMobileUrl = getDynamicLabelValues('dynamic.global_d.lbl_header_bgImage_mobile_url');

  constructor(props) {
    super(props);
    this.state = {
      showCondensedHeader: false,
      isSearchOpen: false,
      isFullSizeSearchModalOpen: false,
      isLoginModalOpen: false,
      headerMiddleNavHeight: 0,
      isShowShopThisStoreBanner: false,
      showCondensedStoreHeader: false,
      isMounted: false,
      isStickyHeader: false,
      lastScrollTop: 0,
      initialTop: 0,
      isSlidingUp: false,
    };
    this.onCloseClick = this.onCloseClick.bind(this);
    this.setSearchState = this.setSearchState.bind(this);
    this.handleScrollThrottle = throttle(this.handleScroll, 100);
    this.handleChangeThrottle = debounce(this.handleChange, 500);
    this.prevScrollpos = 0;
    this.apiConfig = getAPIConfig();
    this.onLoginIconClick = this.onLoginIconClick.bind(this);
    this.checkRoutes = this.checkRoutes.bind(this);
    this.handleHeaderScrollThrottle = throttle(this.handleHeaderScroll, 300);
  }

  static contextType = LabelsContext;

  componentDidMount() {
    this.setState({ isShowShopThisStoreBanner: this.checkRoutes(), isMounted: true });

    if (!this.props.isHeaderScrollViewDisabled) {
      const header = document.getElementById('scrollStickyHeader');
      this.setState({ initialTop: header?.offsetTop || 0 });
      window.addEventListener('scroll', this.handleHeaderScrollThrottle);
    }
    this.addScrollListener();
    const { router } = this.props;
    router.events.on('routeChangeComplete', (url) => this.handleRouteChange(url));
    document.cookie = 'newNav=1';
    this.initialWidth = getViewportInfo()?.width;
    window.addEventListener('resize', this.handleChangeThrottle);
  }

  shouldComponentUpdate(nextProps, nextState) {
    const { favStore } = this.props;
    const userStore = JSON.parse(getLocalStorage('defaultStore')) || '';
    if (
      isEqual(nextProps, this.props) &&
      isEqual(nextState, this.state) &&
      favStore?.basicInfo?.storeName === userStore?.basicInfo?.storeName
    ) {
      return false;
    }
    return true;
  }

  componentDidUpdate(prevProps) {
    this.minicartCondensed();
    if (prevProps.isHeaderScrollViewDisabled !== this.props.isHeaderScrollViewDisabled) {
      if (this.props.isHeaderScrollViewDisabled) {
        window.removeEventListener('scroll', this.handleHeaderScrollThrottle);
      } else {
        const header = document.getElementById('scrollStickyHeader');
        this.setState({ initialTop: header.offsetTop });
        window.addEventListener('scroll', this.handleHeaderScrollThrottle);
      }
    }
  }

  componentWillUnmount() {
    const { router, isHeaderScrollViewDisabled } = this.props;
    if (!isHeaderScrollViewDisabled) {
      window.removeEventListener('scroll', this.handleHeaderScrollThrottle);
    }
    this.removeScrollListener();
    router.events.off('routeChangeComplete', (url) => this.handleRouteChange(url));
    window.removeEventListener('resize', this.handleChangeThrottle);
  }

  onCloseClick() {
    const { isFullSizeSearchModalOpen, isSearchOpen } = this.state;
    this.setState({
      isFullSizeSearchModalOpen: !isFullSizeSearchModalOpen,
      isSearchOpen: !isSearchOpen,
    });

    this.closeNavDrawer();
  }

  onLoginIconClick() {
    requestAnimationFrame(() => {
      const { isLoginModalOpen } = this.state;
      const { trackAnalyticsClick } = this.props;
      if (!isLoginModalOpen) {
        trackAnalyticsClick(
          { nav_cd171: 'header-my-account', customEvents: ['event82'] },
          { name: 'header-my-account' }
        );
      }
      this.calcHeightFromTop();
      this.setState({
        isLoginModalOpen: !isLoginModalOpen,
      });
    });
  }

  setSearchState(currentStatus, cb = null) {
    this.setState({ isSearchOpen: currentStatus }, cb ? cb() : () => {});
  }

  handleChange = (event) => {
    const width = event?.target?.innerWidth;
    if ((this.initialWidth > 1200 && width <= 1200) || (this.initialWidth <= 1200 && width > 1200))
      refreshPage();
  };

  closeNavDrawer = () => {
    const { navigationDrawer, closeNavigationDrawer } = this.props;
    if (navigationDrawer && navigationDrawer.open) {
      closeNavigationDrawer('l1_drawer');
    }
  };

  getStickyPosition = (isPlp) => {
    const header = document.getElementById('header-middle-nav');
    const stickyHeaderPosition = isPlp ? 230 : 0;
    return header && header.offsetTop + stickyHeaderPosition;
  };

  addScrollListener = () => {
    window.addEventListener('scroll', this.handleScrollThrottle);
  };

  removeScrollListener = () => {
    window.removeEventListener('scroll', this.handleScrollThrottle);
  };

  setCondensedHeaderState = (newVal, callback = () => {}) => {
    const { setCondensedState } = this.props;
    const { showCondensedHeader } = this.state;
    if (showCondensedHeader !== newVal) {
      /* this state update done with flush sync because there was an issue with following flow
       move to plp -> go to pdp -> browser back -> condensed header was disappeared
       this was due to experimental scrollRestoration: true added in next config to solve anchoring on plp */
      flushSync(() => {
        this.setState({ showCondensedHeader: newVal });
      });
      const { isDesktop } = getViewportInfo() || {};
      if (!isDesktop) setCondensedState(newVal);
      callback();
    } else if (showCondensedHeader === true && newVal === true) {
      callback();
    }
  };

  isShowCondensedStoreHeader = () => {
    if (
      isShopThisStoreModeOn() &&
      this.state.showCondensedStoreHeader !== this.storeHeaderIsInView()
    ) {
      this.setState({ showCondensedStoreHeader: this.storeHeaderIsInView() });
    }
  };

  getIsScrollEnabled = (url) => {
    this.setState({ isShowShopThisStoreBanner: this.checkRoutes() });
    this.isShowCondensedStoreHeader();
    return url.match(/\/p\//g);
  };

  getIsListingPage = (url) => {
    return url.match(/\/c\//g) || url.match(/\/search\//g) || url.match(/\/category\//g);
  };

  getIsStickyHeader = () => {
    const url = window.location.pathname;
    const isScrollEnabled = this.getIsScrollEnabled(url);
    const { isDesktop, isMobile } = getViewportInfo() || {};
    const isListingPage = isDesktop ? false : this.getIsListingPage(url);
    const sticky = this.getStickyPosition(isListingPage);
    const isSticky = window.pageYOffset > sticky;

    if (isMobile && isScrollEnabled) {
      return window.pageYOffset && isSticky;
    }

    return (isDesktop && window.pageYOffset > sticky + 64) || (!isDesktop && isSticky);
  };

  minicartCondensed = () => {
    const condensedHeaderMini = document.getElementsByClassName('header-global')[0];
    const condensedHeader = document.getElementsByClassName('condensed-header')[0];
    const BAGCONDENSED = 'show-condensed-minibag';
    if (condensedHeaderMini) {
      if (condensedHeader) {
        if (condensedHeaderMini && !condensedHeaderMini.classList.contains(BAGCONDENSED)) {
          condensedHeaderMini.classList.add(BAGCONDENSED);
        }
      } else {
        condensedHeaderMini.classList.remove(BAGCONDENSED);
      }
    }
  };

  storeHeaderIsInView = () => {
    const storeHeader = document.getElementById('store-details');
    const bounding = storeHeader?.getBoundingClientRect();

    if (
      bounding &&
      bounding.top >= 0 &&
      bounding.left >= 0 &&
      bounding.right <= (window.innerWidth || document.documentElement.clientWidth) &&
      bounding.bottom <= (window.innerHeight || document.documentElement.clientHeight)
    ) {
      return false;
    }
    return true;
  };

  handleScroll = () => {
    /**
     * Note:
     * 1. Condensed header is 'stuck' if scrolled past Navigation bar
     * and scrolling direction is down
     * 2. Condensed header is 'unstuck' when scrolling up till Navigation bar get visible
     */
    let showGiftCard = true;
    const { isMounted } = this.state;
    if (isMounted && this.getIsStickyHeader()) {
      this.setCondensedHeaderState(true, () => {
        const condensedHeader = document.getElementsByClassName('condensed-header')[0];
        if (condensedHeader && !doesCondensedClassExists(condensedHeader)) {
          condensedHeader.classList.add('show-condensed-header');
          this.minicartCondensed();
        }
        /*  This is to show Gift Cards Navigation Tab - code written in Launch
            https://launch.adobe.com/companies/CO5481a39341114faeaef51e2d4476320d/properties/PR0951db90dcc84cb683be4f6ff29a7843/rules/RL755519950c81466486b98df079adf431
          */
        if (showGiftCard && typeof window.addGiftNav === 'function') {
          window.addGiftNav();
          showGiftCard = false;
        }
      });
    } else {
      this.setCondensedHeaderState(false);
    }
    this.prevScrollpos = isMounted && window.pageYOffset;
    this.isShowCondensedStoreHeader();
  };

  handleHeaderScroll = () => {
    const { lastScrollTop, initialTop, isStickyHeader } = this.state;
    const currentScrollY = window.pageYOffset;
    const dropDownContainer = document.getElementById('dropdown-container');
    const dropDownContainerOpen =
      dropDownContainer && dropDownContainer.classList.contains('dropdown-open');
    const accountDrawerOpen = document.getElementsByClassName('account-drawer-modal')[0];
    if (
      (currentScrollY < lastScrollTop && currentScrollY > initialTop && !isStickyHeader) ||
      dropDownContainerOpen ||
      accountDrawerOpen
    ) {
      this.setState({ isStickyHeader: true, isSlidingUp: false });
    } else if (lastScrollTop !== 0 && currentScrollY > lastScrollTop + 10 && isStickyHeader) {
      this.setState({ isSlidingUp: true });
      setTimeout(() => {
        this.setState({ isStickyHeader: false, isSlidingUp: false });
      }, 300);
    } else if (currentScrollY <= initialTop && isStickyHeader) {
      this.setState({ isStickyHeader: false, isSlidingUp: false });
    }

    this.setState({ lastScrollTop: currentScrollY });
  };

  handleRouteChange(asPath) {
    const isScrollEnabled = this.getIsScrollEnabled(asPath);
    if (isScrollEnabled) {
      this.prevScrollpos = 0;
      this.setCondensedHeaderState(false);
    }
  }

  flattenSet = (set) => {
    const flatSet = {};
    if (set && Array.isArray(set)) {
      const setLength = set.length;
      for (let i = 0; i < setLength; i += 1) {
        flatSet[set[i].key] = set[i].val;
      }
    }
    return flatSet;
  };

  renderCountDown = (countdown) => {
    return countdown && countdown.length > 0 ? <ModuleCountDown countdown={countdown[0]} /> : null;
  };

  renderSlider = (slider, position, sliderVariation) => {
    const cookieID = position === SLIDER_POSITION_TOP_NAV ? 'topHeaderSlider' : '';
    return slider && slider.length > 0 ? (
      <ModuleSlider
        slider={slider}
        identifier={position}
        cookieID={cookieID}
        sliderVariation={sliderVariation}
      />
    ) : null;
  };

  renderHeaderPromo = (isBagPage, isShowBagSimplifiedHeader) => {
    /**
     * Note:
     * Mobile header to be rendered  for mobile viewport and desktop for larger devices
     */
    const { headerPromoTextArea, headerPromoHtmlArea } = this.props;
    const isHideGlobalPromo = isBagPage && isShowBagSimplifiedHeader;
    return (
      <>
        <HeaderPromo
          mobileMarkup
          isHideGlobalPromo={isHideGlobalPromo}
          className="header__promo-area--mobile show-promo-on-mobile"
          dataTextPromo={headerPromoTextArea}
          dataHtmlPromo={headerPromoHtmlArea}
          placeCashMobileUrl={this.placeCashMobileUrl}
          placeCashDesktopUrl={this.placeCashDesktopUrl}
        />

        <HeaderPromo
          className="header__promo-area--desktop hide-on-mobile"
          dataTextPromo={headerPromoTextArea}
          dataHtmlPromo={headerPromoHtmlArea}
          placeCashMobileUrl={this.placeCashMobileUrl}
          placeCashDesktopUrl={this.placeCashDesktopUrl}
        />
      </>
    );
  };

  renderLoginModalDynamically = () => {
    const { isLoggedIn, loginModalMountedState, setLoginModalMountState } = this.props;
    if (!isLoggedIn && loginModalMountedState) {
      return (
        <DynamicModule
          key="login-fav-modal"
          importCallback={() =>
            import(
              /* webpackChunkName: "login-fav-modal" */ '@tcp/core/src/components/features/account/LoginPage/views/LoginModal'
            )
          }
          variation="favorites"
          setLoginModalMountState={setLoginModalMountState}
          openState={loginModalMountedState}
        />
      );
    }
    return null;
  };

  calcHeightFromTop = () => {
    const node = document.getElementById('headerMiddleNav');
    if (node) {
      const elDistanceToTop = node.getBoundingClientRect().top + node.offsetHeight;
      this.setState({
        headerMiddleNavHeight: elDistanceToTop,
      });
    }
  };

  checkRoutes = () => {
    const { router } = this.props;
    if (SHOP_THIS_STORE_ACCESSIBLE_COMPONENTS.SHOP_THIS_STORE === router?.state?.pathname)
      return true;
    return isShopThisStoreModeOn();
  };

  renderNotification = () => {
    return (
      <Notification
        className="shop-thi-store-notification"
        status="success"
        message={renderLabel(
          this.globalLabels?.header?.lbl_shopThisStore_notificationMessage,
          'lbl_shopThisStore_notificationMessage'
        )}
        isCloseable={true}
        onClose={() => this?.props?.shopThisStoreNotification(false)}
      />
    );
  };

  renderShopThisStoreCondensedHeader = (isHeaderScrollViewDisabled) => {
    const { extraConfig } = this.context;
    const { isHeaderStickyClassPresent, stickyHeaderHeight } = extraConfig || {};
    const { favStore } = this.props;
    return (
      <HeaderShopThisStoreAreaCondensed
        stickyHeaderHeight={stickyHeaderHeight}
        isHeaderScrollViewDisabled={isHeaderScrollViewDisabled}
        isHeaderStickyClassPresent={isHeaderStickyClassPresent}
        trackClickAnalyticsBopisFilter={this.props.trackClickAnalyticsBopisFilter}
        router={this.props.router}
        trackAnalyticsClick={this.props.trackAnalyticsClick}
        favStore={favStore}
      />
    );
  };

  // eslint-disable-next-line sonarjs/cognitive-complexity
  render() {
    const {
      className,
      brandTabs,
      navigationDrawer,
      openNavigationDrawer,
      closeNavigationDrawer,
      isUserGuest,
      isUserPlcc,
      isRememberedUser,
      userName,
      userPoints,
      userRewards,
      openOverlay,
      closedOverlay,
      isOpenOverlay,
      isLoggedIn,
      cartItemCount,
      openMiniBagDispatch,
      closeMiniBagDispatch,
      totalItems,
      favStore,
      isPickupModalOpen,
      loyaltyPromoBanner,
      setClickAnalyticsData,
      isQVModalOpen,
      onRouteChangeGlobalActions,
      isOpenAddedToBag,
      isCartItemSFL,
      router,
      showMiniBag,
      trackOrderMountedState,
      getOrderDetailsDispatch,
      setUpdateMiniBagFlag,
      orderItems,
      updateMiniBag,
      isECOM,
      isRecalculateTaxes,
      showFullPageAuth,
      isShowNavAnimation,
      isKeepMinibagCloseState,
      updateMiniBagCloseStatusActn,
      isKeepMinibagCloseStateQV,
      updateMiniBagCloseStatusActnQV,
      composites: { countdown, slider },
      headerSet,
      isExtendedPage,
      isNewNavigationImagesEnabled,
      trackClickAnalyticsBopisFilter,
      setFavData,
      userWishListId,
      defaultWishList,
      activeWishList,
      brandId,
      trackAnalyticsClick,
      trackStsClick,
      isSearchEnhancementRedesignEnabled,
      checkNavImagesFlag,
      isDrawerOpen,
      isShopThisStoreEnabled,
      accountDrawerOpen,
      openAccountDrawerDispatch,
      closeAccountDrawerDispatch,
      topPromoBanner,
      defaultStore,
      navigationDrawer: { close = true } = {},
      isHamburgerMenuRedesignEnabled,
      isGuestFavEnabled,
      convertedBossUnavailableList,
      isConfirmationPageRoute,
      isShowBagSimplifiedHeader,
      isHeaderScrollViewDisabled,
      isDrawerTransitionEnabled,
      isStickyHeaderBgColorEnabled,
      resetCurrentStoreInfo,
      isSearchBarAlignmentEnabled,
    } = this.props;
    const { extraConfig } = this.context;
    const { isHeaderStickyClassPresent } = extraConfig || {};
    const isBagPage = router?.asPath?.includes('/bag');
    const isShopThisStorePage = router?.asPath?.includes('shop-this-store');

    const {
      showCondensedHeader,
      isSearchOpen,
      isFullSizeSearchModalOpen,
      isLoginModalOpen,
      headerMiddleNavHeight,
      showCondensedStoreHeader,
      isStickyHeader,
      isSlidingUp,
    } = this.state;

    const { countdownPosition, sliderPosition, sliderVariation } = this.flattenSet(headerSet);
    return (
      <React.Fragment>
        {!isDrawerOpen ? (
          <div className="header-top-nav-wrapper">
            <SkipMain href="#overlayWrapper" noLink className="skip-main">
              {renderLabel(this.globalLabels?.accessibility?.skipNavigation, 'skipNavigation')}
            </SkipMain>
            {loyaltyPromoBanner && close && !isConfirmationPageRoute && (
              <LoyaltyPromoBanner
                richTextList={topPromoBanner}
                className="header-promo__container top-promo-banner"
                cookieID="mprTopHead"
                isUserGuest={isUserGuest}
                isUserPlcc={isUserPlcc}
                isLoggedIn={isLoggedIn}
              />
            )}
            {sliderPosition === SLIDER_POSITION_TOP_NAV && !isConfirmationPageRoute
              ? this.renderSlider(slider, sliderPosition, sliderVariation)
              : null}
            <div className="header-topnav-countdown">
              {countdownPosition === COUNTDOWN_POSITION_TOP_NAV && !isConfirmationPageRoute
                ? this.renderCountDown(countdown)
                : null}
            </div>
          </div>
        ) : (
          <></>
        )}
        <HeaderTag
          id="scrollStickyHeader"
          isHeaderStickyClassPresent={isHeaderStickyClassPresent}
          className={`${!isHeaderScrollViewDisabled && isStickyHeader ? 'header-sticky' : ''} ${
            isSlidingUp ? 'slide-up' : ''
          }`}
          isDrawerOpen={isDrawerOpen}
        >
          <header className={`${className} header-global ${isCartItemSFL ? 'removeX-hidden' : ''}`}>
            <HeaderMiddleNav
              showCondensedHeader={isHeaderScrollViewDisabled && showCondensedHeader}
              openNavigationDrawer={openNavigationDrawer}
              closeNavigationDrawer={closeNavigationDrawer}
              navigationDrawer={navigationDrawer}
              isUserPlcc={isUserPlcc}
              isRememberedUser={isRememberedUser}
              userName={userName}
              userPoints={userPoints}
              userRewards={userRewards}
              openOverlay={openOverlay}
              closedOverlay={closedOverlay}
              isOpenOverlay={isOpenOverlay}
              isLoggedIn={isLoggedIn}
              cartItemCount={cartItemCount}
              totalItems={totalItems}
              openMiniBagDispatch={openMiniBagDispatch}
              closeMiniBagDispatch={closeMiniBagDispatch}
              store={favStore}
              setClickAnalyticsData={setClickAnalyticsData}
              onRouteChangeGlobalActions={onRouteChangeGlobalActions}
              isSearchOpen={isSearchOpen}
              isFullSizeSearchModalOpen={isFullSizeSearchModalOpen}
              isLoginModalOpen={isLoginModalOpen}
              onCloseClick={this.onCloseClick}
              onLoginIconClick={this.onLoginIconClick}
              setSearchState={this.setSearchState}
              isOpenAddedToBag={isOpenAddedToBag}
              showMiniBag={showMiniBag}
              getOrderDetailsDispatch={getOrderDetailsDispatch}
              orderItems={orderItems}
              updateMiniBag={updateMiniBag}
              setUpdateMiniBagFlag={setUpdateMiniBagFlag}
              isECOM={isECOM}
              isRecalculateTaxes={isRecalculateTaxes}
              showFullPageAuth={showFullPageAuth}
              minibagOnHoverEnabled={true}
              isKeepMinibagCloseState={isKeepMinibagCloseState}
              updateMiniBagCloseStatusActn={updateMiniBagCloseStatusActn}
              brandTabs={brandTabs}
              brandId={brandId}
              trackClickAnalyticsBopisFilter={trackClickAnalyticsBopisFilter}
              setFavData={setFavData}
              userWishListId={userWishListId}
              defaultWishList={defaultWishList}
              activeWishList={activeWishList}
              headerMiddleNavHeight={headerMiddleNavHeight}
              calcHeightFromTop={this.calcHeightFromTop}
              trackAnalyticsClick={trackAnalyticsClick}
              trackStsClick={trackStsClick}
              isNewNavigationImagesEnabled={isNewNavigationImagesEnabled}
              isSearchEnhancementRedesignEnabled={isSearchEnhancementRedesignEnabled}
              checkNavImagesFlag={checkNavImagesFlag}
              isShopThisStoreEnabled={isShopThisStoreEnabled}
              accountDrawerOpen={accountDrawerOpen}
              openAccountDrawerDispatch={openAccountDrawerDispatch}
              closeAccountDrawerDispatch={closeAccountDrawerDispatch}
              defaultStore={defaultStore}
              isHamburgerMenuRedesignEnabled={isHamburgerMenuRedesignEnabled}
              isGuestFavEnabled={isGuestFavEnabled}
              convertedBossUnavailableList={convertedBossUnavailableList}
              isDrawerTransitionEnabled={isDrawerTransitionEnabled}
              isHeaderStickyNav={!isHeaderScrollViewDisabled && isStickyHeader}
              isHeaderScrollViewDisabled={isHeaderScrollViewDisabled}
              isStickyHeaderBgColorEnabled={
                !isHeaderScrollViewDisabled && isStickyHeaderBgColorEnabled
              }
              resetCurrentStoreInfo={resetCurrentStoreInfo}
              isSearchBarAlignmentEnabled={isSearchBarAlignmentEnabled}
            />
          </header>
        </HeaderTag>
        <RenderPerf.Measure name={NAVIGATION_VISIBLE} />
        {isOpenOverlay && (
          <DynamicModule
            key="overlay-modal"
            importCallback={() =>
              import(
                /* webpackChunkName: "overlay-modal" */ '@tcp/core/src/components/features/account/OverlayModal'
              )
            }
            showCondensedHeader={showCondensedHeader}
            isLoggedIn={isLoggedIn}
            openState={isOpenOverlay}
          />
        )}
        {!isConfirmationPageRoute && this.renderHeaderPromo(isBagPage, isShowBagSimplifiedHeader)}
        {loyaltyPromoBanner && !isConfirmationPageRoute && (
          <DynamicModule
            key="loyalty-promo-banner"
            // eslint-disable-next-line sonarjs/no-identical-functions
            importCallback={() =>
              import(
                /* webpackChunkName: "loyalty-promo-banner" */ '@tcp/core/src/components/common/molecules/LoyaltyPromoBanner'
              )
            }
            richTextList={loyaltyPromoBanner}
            className="header-promo__container"
            cookieID="mprAboveHead"
            isUserGuest={isUserGuest}
            isUserPlcc={isUserPlcc}
          />
        )}

        {countdownPosition === COUNTDOWN_POSITION_SUB_NAV && !isConfirmationPageRoute ? (
          <div className="countdown-wrapper content-wrapper">{this.renderCountDown(countdown)}</div>
        ) : null}

        {sliderPosition === SLIDER_POSITION_SUB_NAV && !isConfirmationPageRoute
          ? this.renderSlider(slider, sliderPosition, sliderVariation)
          : null}

        {this?.props?.showShopThisStoreNotification &&
          !isConfirmationPageRoute &&
          this.renderNotification()}

        {isHeaderScrollViewDisabled && showCondensedHeader && (
          <CondensedHeader
            openNavigationDrawer={openNavigationDrawer}
            openMiniBagDispatch={openMiniBagDispatch}
            closeMiniBagDispatch={closeMiniBagDispatch}
            closeNavigationDrawer={closeNavigationDrawer}
            navigationDrawer={navigationDrawer}
            userName={userName}
            userPoints={userPoints}
            userRewards={userRewards}
            openOverlay={openOverlay}
            isLoggedIn={isLoggedIn}
            cartItemCount={cartItemCount}
            totalItems={totalItems}
            showCondensedHeader={showCondensedHeader}
            store={favStore}
            onCloseClick={this.onCloseClick}
            onLoginIconClick={this.onLoginIconClick}
            setSearchState={this.setSearchState}
            isLoginModalOpen={isLoginModalOpen}
            isSearchOpen={isSearchOpen}
            isOpenAddedToBag={isOpenAddedToBag}
            getOrderDetailsDispatch={getOrderDetailsDispatch}
            orderItems={orderItems}
            updateMiniBag={updateMiniBag}
            setUpdateMiniBagFlag={setUpdateMiniBagFlag}
            isECOM={isECOM}
            isRecalculateTaxes={isRecalculateTaxes}
            showFullPageAuth={showFullPageAuth}
            minibagOnHoverEnabled={true}
            isUserPlcc={isUserPlcc}
            isShowNavAnimation={isShowNavAnimation}
            isKeepMinibagCloseState={isKeepMinibagCloseState}
            updateMiniBagCloseStatusActn={updateMiniBagCloseStatusActn}
            isKeepMinibagCloseStateQV={isKeepMinibagCloseStateQV}
            updateMiniBagCloseStatusActnQV={updateMiniBagCloseStatusActnQV}
            isExtendedPage={isExtendedPage}
            setFavData={setFavData}
            userWishListId={userWishListId}
            defaultWishList={defaultWishList}
            activeWishList={activeWishList}
            headerMiddleNavHeight={headerMiddleNavHeight}
            setClickAnalyticsData={setClickAnalyticsData}
            calcHeightFromTop={this.calcHeightFromTop}
            closedOverlay={closedOverlay}
            brandId={brandId}
            trackClickAnalyticsBopisFilter={trackClickAnalyticsBopisFilter}
            trackAnalyticsClick={trackAnalyticsClick}
            trackStsClick={trackStsClick}
            isRememberedUser={isRememberedUser}
            isSearchEnhancementRedesignEnabled={isSearchEnhancementRedesignEnabled}
            isShopThisStoreEnabled={isShopThisStoreEnabled}
            isNewNavigationImagesEnabled={isNewNavigationImagesEnabled}
            accountDrawerOpen={accountDrawerOpen}
            openAccountDrawerDispatch={openAccountDrawerDispatch}
            closeAccountDrawerDispatch={closeAccountDrawerDispatch}
            isHamburgerMenuRedesignEnabled={isHamburgerMenuRedesignEnabled}
            isGuestFavEnabled={isGuestFavEnabled}
          />
        )}
        {this.state?.isShowShopThisStoreBanner &&
          showCondensedStoreHeader &&
          showCondensedHeader &&
          !isConfirmationPageRoute &&
          this.renderShopThisStoreCondensedHeader(isHeaderScrollViewDisabled)}
        {trackOrderMountedState && (
          <DynamicModule
            key="track-order"
            importCallback={() =>
              import(
                /* webpackChunkName: "track-order-modal" */ '@tcp/core/src/components/features/account/TrackOrder'
              )
            }
            trackOrderMountedState={trackOrderMountedState}
          />
        )}
        {isPickupModalOpen && (
          <DynamicModule
            key="pickup-modal"
            importCallback={() =>
              import(
                /* webpackChunkName: "pick-store-modal" */ '@tcp/core/src/components/common/organisms/PickupStoreModal'
              )
            }
            router={router}
          />
        )}
        {this.renderLoginModalDynamically()}
        {isQVModalOpen && (
          <DynamicModule
            key="quickview-modal"
            importCallback={() =>
              import(
                /* webpackChunkName: "quick-view-modal" */ '@tcp/core/src/components/common/organisms/QuickViewModal/container/QuickViewModal.container'
              )
            }
          />
        )}
        {isShopThisStorePage && (
          <>
            {this.state.isShowShopThisStoreBanner ? (
              <HeaderShopThisStoreArea
                key={router?.state?.asPath}
                trackClickAnalyticsBopisFilter={trackClickAnalyticsBopisFilter}
                router={router}
                trackAnalyticsClick={trackAnalyticsClick}
                favStore={favStore}
              />
            ) : (
              <HeaderShopThisStoreSkeleton />
            )}
          </>
        )}
      </React.Fragment>
    );
  }
}

Header.propTypes = {
  className: PropTypes.string.isRequired,
  loyaltyPromoBanner: PropTypes.arrayOf(PropTypes.shape({})),
  brandTabs: PropTypes.string,
  topPromoBanner: PropTypes.arrayOf(PropTypes.shape({})),
  promoMessageWrapper: PropTypes.arrayOf(PropTypes.shape({})),
  headerPromoTextArea: PropTypes.arrayOf(PropTypes.shape({})),
  headerPromoHtmlArea: PropTypes.arrayOf(PropTypes.shape({})),
  navigationDrawer: PropTypes.arrayOf(PropTypes.object),
  openNavigationDrawer: PropTypes.func.isRequired,
  closeNavigationDrawer: PropTypes.func.isRequired,
  getOrderDetailsDispatch: PropTypes.func,
  isUserGuest: PropTypes.bool,
  isUserPlcc: PropTypes.bool,
  userName: PropTypes.string,
  userPoints: PropTypes.string,
  userRewards: PropTypes.string,
  openOverlay: PropTypes.func.isRequired,
  closedOverlay: PropTypes.func.isRequired,
  isOpenOverlay: PropTypes.bool.isRequired,
  isLoggedIn: PropTypes.bool,
  cartItemCount: PropTypes.number.isRequired,
  openMiniBagDispatch: PropTypes.func.isRequired,
  closeMiniBagDispatch: PropTypes.func.isRequired,
  setUpdateMiniBagFlag: PropTypes.func,
  updateMiniBag: PropTypes.bool,
  totalItems: PropTypes.number,
  favStore: PropTypes.shape({
    basicInfo: PropTypes.shape({}),
    hours: PropTypes.shape({
      regularHours: PropTypes.shape([]),
      regularAndHolidayHours: PropTypes.shape([]),
      holidayHours: PropTypes.shape([]),
    }),
    features: PropTypes.shape({}),
  }),
  isPickupModalOpen: PropTypes.bool,
  isRememberedUser: PropTypes.bool,
  setClickAnalyticsData: PropTypes.func.isRequired,
  isQVModalOpen: PropTypes.bool,
  onRouteChangeGlobalActions: PropTypes.func,
  isOpenAddedToBag: PropTypes.bool,
  isCartItemSFL: PropTypes.bool,
  router: PropTypes.shape({}),
  orderItems: PropTypes.arrayOf(PropTypes.shape({})),
  showMiniBag: PropTypes.bool,
  trackOrderMountedState: PropTypes.bool,
  isRecalculateTaxes: PropTypes.bool,
  isECOM: PropTypes.bool,
  showFullPageAuth: PropTypes.bool,
  isCSHEnabled: PropTypes.bool,
  selectOrderAction: PropTypes.func.isRequired,
  loginModalMountedState: PropTypes.bool,
  setLoginModalMountState: PropTypes.func.isRequired,
  trackAnalyticsClick: PropTypes.func,
  resetEmailConfirmationRequest: PropTypes.func,
  mergeAccountReset: PropTypes.func,
  isShowNavAnimation: PropTypes.bool,
  isKeepMinibagCloseState: PropTypes.bool,
  isKeepMinibagCloseStateQV: PropTypes.bool,
  updateMiniBagCloseStatusActn: PropTypes.func,
  updateMiniBagCloseStatusActnQV: PropTypes.func,
  composites: PropTypes.shape({
    countdown: PropTypes.arrayOf(PropTypes.shape({})),
    slider: PropTypes.arrayOf(PropTypes.shape({})),
  }),
  headerSet: PropTypes.arrayOf(PropTypes.shape({})),
  isExtendedPage: PropTypes.bool,
  setFavData: PropTypes.func,
  userWishListId: PropTypes.string,
  defaultWishList: PropTypes.shape({}),
  activeWishList: PropTypes.shape({}),
  setCondensedState: PropTypes.func,
  isShopThisStoreEnabled: PropTypes.bool,
  openAccountDrawerDispatch: PropTypes.func.isRequired,
  closeAccountDrawerDispatch: PropTypes.func.isRequired,
  accountDrawerOpen: PropTypes.bool,
  isGuestFavEnabled: PropTypes.bool,
  isHeaderScrollViewDisabled: PropTypes.bool,
  isDrawerTransitionEnabled: PropTypes.bool,
  isStickyHeaderBgColorEnabled: PropTypes.bool,
};

Header.defaultProps = {
  totalItems: 0,
  favStore: {
    basicInfo: {
      id: '',
      storeName: '',
      phone: '',
      coordinates: {},
      address: {},
    },
    hours: {
      regularHours: [],
      regularAndHolidayHours: [],
      holidayHours: [],
    },
    features: {},
  },
  isPickupModalOpen: false,
  isRememberedUser: false,
  isQVModalOpen: false,
  onRouteChangeGlobalActions: () => {},
  isOpenAddedToBag: false,
  isCartItemSFL: false,
  router: {},
  showMiniBag: false,
  trackOrderMountedState: false,
  isRecalculateTaxes: false,
  isECOM: false,
  showFullPageAuth: false,
  isCSHEnabled: false,
  trackAnalyticsClick: () => {},
  isShowNavAnimation: false,
  resetEmailConfirmationRequest: () => {},
  mergeAccountReset: () => {},
  loyaltyPromoBanner: [],
  brandTabs: '',
  topPromoBanner: [],
  promoMessageWrapper: [],
  headerPromoTextArea: [],
  headerPromoHtmlArea: [],
  navigationDrawer: [],
  getOrderDetailsDispatch: () => {},
  isUserGuest: true,
  isUserPlcc: false,
  userName: '',
  userPoints: '',
  userRewards: '',
  isLoggedIn: false,
  setUpdateMiniBagFlag: () => {},
  updateMiniBag: false,
  orderItems: [],
  isKeepMinibagCloseState: false,
  isKeepMinibagCloseStateQV: false,
  updateMiniBagCloseStatusActn: () => {},
  updateMiniBagCloseStatusActnQV: () => {},
  composites: {
    countdown: {},
    slider: {},
  },
  headerSet: [],
  isExtendedPage: false,
  setFavData: () => {},
  userWishListId: '',
  defaultWishList: null,
  activeWishList: null,
  setCondensedState: () => {},
  isShopThisStoreEnabled: false,
  accountDrawerOpen: false,
  isGuestFavEnabled: false,
  isHeaderScrollViewDisabled: false,
  isDrawerTransitionEnabled: false,
  isStickyHeaderBgColorEnabled: false,
};

export default errorBoundary(withStyles(Header, style));
export { Header as HeaderVanilla };
